import '@assets/styles/tiptap-preview.css';

import {
  Chat,
  LinkPreviewResponse,
  PinnedChat,
  ThreadChat,
} from '@src/api/shared/community/chatApiClient';
import { ChannelMemberUserTypeEnum } from '@src/enum/ChannelMemberUserTypeEnum';
import ChatActionMenuPc from '@src/features/shared/community/components/chat/ChatActionMenuPc';
import ChatActionMenuSp from '@src/features/shared/community/components/chat/ChatActionMenuSp';
import ChatContent from '@src/features/shared/community/components/chat/ChatContent';
import { useChatMessageDisplay } from '@src/features/shared/community/components/chat/hooks/useChatMessageDisplay';
import { copyChatLinkToClipboard } from '@src/utils/urlUtils';
import React, { Fragment } from 'react';

import { ChatEditingMessage } from './ChatEditingMessage';
import ChatItemHeader from './chatList/ChatItemHeader';
import Tooltip from './Tooltip';

export type ChatMenuPermission = {
  canShowKebabMenu: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canReport: boolean;
};

// Group related props for better organization
export type ChatContextData = {
  chat: Chat | ThreadChat;
  pinnedChat?: PinnedChat;
  linkPreviewCache: Record<string, LinkPreviewResponse | null>;
  isThread?: boolean;
};

export type ChatUserState = {
  isLoggedIn: boolean;
  currentUserId?: number;
  currentUserType?: ChannelMemberUserTypeEnum;
};

export type ChatUIState = {
  editingChatId: number | null;
  isActionMenuSpOpen: boolean;
  currentChatIdSp: number | undefined;
};

export type ChatCallbacks = {
  onPinClick: (chat_id: number | undefined) => Promise<void>;
  onChatEditClick: (chat: Chat | ThreadChat) => void;
  onChatDeleteClick: (chat: Chat | ThreadChat) => void;
  onChatReportClick: (chat: Chat | ThreadChat) => void;
  onPinnedChatMenuClose: () => void;
  onSetCurrentChatIdSp: (chatId: number) => void;
  onPinnedChatLongPress: () => void;
  onToggleLinkPreview: (chatId: number, hidePreview: boolean) => void;
  onOpenThread?: (chatId: number) => void;
};

type ChatMessageProps = {
  contextData: ChatContextData;
  userState: ChatUserState;
  uiState: ChatUIState;
  callbacks: ChatCallbacks;
  getChatMenuPermission: (chat: Chat | ThreadChat) => ChatMenuPermission;
  chatRef: (el: HTMLDivElement | null) => void;
};

const ChatMessage: React.FC<ChatMessageProps> = ({
  contextData,
  userState,
  uiState,
  callbacks,
  getChatMenuPermission,
  chatRef,
}) => {
  const { chat, pinnedChat, linkPreviewCache, isThread: isThreadContext } = contextData;
  const { isLoggedIn, currentUserId, currentUserType } = userState;
  const { editingChatId, isActionMenuSpOpen, currentChatIdSp } = uiState;
  const {
    onPinClick,
    onChatEditClick,
    onChatDeleteClick,
    onChatReportClick,
    onPinnedChatMenuClose,
    onSetCurrentChatIdSp,
    onPinnedChatLongPress,
    onToggleLinkPreview,
    onOpenThread,
  } = callbacks;
  const {
    mentionClickedChatId,
    setMentionClickedChatId,
    tooltipRef,
    showTooltip,
    displayTooltip,
    preview,
    attachments,
    htmlWithMentions,
    reactions,
    longPressBind,
    isHtmlEmpty,
  } = useChatMessageDisplay({
    chat,
    isLoggedIn,
    currentUserId,
    currentUserType,
    linkPreviewCache,
    onSetCurrentChatIdSp,
    onPinnedChatLongPress,
  });

  const isOwner =
    isLoggedIn && chat.user_id === currentUserId && chat.user_type === currentUserType;

  return (
    <Fragment key={chat.id}>
      <div
        ref={chatRef}
        className='relative flex flex-col gap-4 px-6 py-3'
        {...longPressBind(chat.id)}
      >
        <ChatEditingMessage
          className='absolute -left-px top-[-31px] grid h-[30px] w-[160px] place-items-center bg-[#D9D9D9] text-sm'
          isEditing={editingChatId === chat.id}
        />

        <ChatItemHeader
          chat={chat}
          pinnedChat={pinnedChat}
          isLoggedIn={isLoggedIn}
        />

        <ChatContent
          chat={chat}
          htmlWithMentions={htmlWithMentions}
          preview={preview}
          attachments={attachments}
          reactions={reactions}
          setMentionClickedChatId={setMentionClickedChatId}
          displayTooltip={displayTooltip}
          isHtmlEmpty={isHtmlEmpty}
          isOwner={isOwner}
          onTogglePreview={(hidePreview) => onToggleLinkPreview(chat.id, hidePreview)}
        />

        <div className='absolute right-6 top-1 hidden group-hover:block lg:!hidden'>
          <ChatActionMenuPc
            chatId={chat.id}
            handlePinClick={() => onPinClick(pinnedChat?.id === chat.id ? undefined : chat.id)}
            handleChatLinkCopyClick={() =>
              copyChatLinkToClipboard(chat.id, { isThread: isThreadContext ?? !('date_label' in chat) })
            }
            handleChatEditClick={() => onChatEditClick(chat)}
            handleChatDeleteClick={() => onChatDeleteClick(chat)}
            handleChatReportClick={() => onChatReportClick(chat)}
            handleOpenThread={onOpenThread ? () => onOpenThread(chat.id) : undefined}
            isPinned={pinnedChat?.id === chat.id}
            chatMenuPermission={getChatMenuPermission(chat)}
            currentReactions={reactions}
          />
        </div>
        {isActionMenuSpOpen && currentChatIdSp === chat.id && (
          <ChatActionMenuSp
            chatId={currentChatIdSp}
            handleCloseClick={onPinnedChatMenuClose}
            handleChatLinkCopyClick={() =>
              copyChatLinkToClipboard(chat.id, { isThread: isThreadContext ?? !('date_label' in chat) })
            }
            handlePinClick={() =>
              onPinClick(pinnedChat?.id === currentChatIdSp ? undefined : currentChatIdSp)
            }
            handleChatEditClick={() => onChatEditClick(chat)}
            handleChatDeleteClick={() => onChatDeleteClick(chat)}
            handleChatReportClick={() => onChatReportClick(chat)}
            handleOpenThread={onOpenThread ? () => onOpenThread(chat.id) : undefined}
            isPinned={pinnedChat?.id === chat.id}
            chatMenuPermission={getChatMenuPermission(chat)}
            isLoggedIn={isLoggedIn}
            currentReactions={reactions}
          />
        )}
        {showTooltip && mentionClickedChatId === chat.id && (
          <div
            ref={tooltipRef}
            className='absolute left-1/2 top-1/2 z-[999] -translate-x-1/2 -translate-y-1/2 text-[12px] lg:left-[40%] lg:ml-[17%] lg:w-[290px]'
          >
            <Tooltip
              label='表示するには、ログインしてください。'
              weight='500'
            />
          </div>
        )}
      </div>
    </Fragment>
  );
};

export default ChatMessage;
